import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Table } from 'semantic-ui-react';
import endOfDay from 'date-fns/endOfDay';
import startOfDay from 'date-fns/startOfDay';
import isAfter from 'date-fns/isAfter';
import HasSomeRights from 'nallian-shared-ui/lib/layout/rights/HasSomeRights';
import { history } from 'nallian-shared-ui/lib/config/history';
import { formatDate } from 'nallian-shared-ui/lib/lib/dateTime';
import { Icon, ConfirmModal } from 'nallian-shared-ui/lib/components';
import { APP_SUBSCRIPTION_ACTIONS } from '../../constants';
import { ValidPeriodWrapper } from '../../../app/styles';

const ORGANISATION_APP_RIGHTS = [
  'ORGANISATIONAPPS.MANAGE.ALL',
  'ORGANISATIONAPPS.MANAGE.ALL.EXCEPTSYSTEMORGS',
  'ORGANISATIONAPPS.MANAGE.ORGANISATION',
];

const AppSubscriptionFeature = ({
  forbidManageBecauseSystemOrg,
  urlRoot,
  organisationApp,
  feature,
  handleRemoveFeature,
}) => {
  const { t } = useTranslation();

  const handleEditFeature = useCallback(
    () =>
      history.push(
        `${urlRoot}/app-subscriptions/${APP_SUBSCRIPTION_ACTIONS.EDIT_FEATURE}/${feature.appInstallationFeature?.tag}?communityAppId=${organisationApp.id}`
      ),
    [feature, organisationApp, urlRoot]
  );

  const onRemoveFeature = useCallback(
    () => handleRemoveFeature(undefined, { feature, organisationApp }),
    [handleRemoveFeature, organisationApp, feature]
  );

  return (
    <Table.Row>
      <Table.Cell>{feature.alias || feature.appInstallationFeature?.name}</Table.Cell>
      <Table.Cell>
        {feature.communityAppFeatureSubscription?.validFrom &&
        feature.communityAppFeatureSubscription?.validUntil ? (
          <ValidPeriodWrapper
            red={isAfter(
              endOfDay(new Date()),
              new Date(feature.communityAppFeatureSubscription.validUntil)
            )}
            green={isAfter(
              startOfDay(new Date()),
              new Date(feature.communityAppFeatureSubscription.validFrom)
            )}
          >
            {formatDate(feature.communityAppFeatureSubscription.validFrom)} -{' '}
            {formatDate(feature.communityAppFeatureSubscription.validUntil)}
          </ValidPeriodWrapper>
        ) : feature.requiresSubscription ? (
          '- / -'
        ) : (
          t('noValidPeriod')
        )}
      </Table.Cell>
      <Table.Cell textAlign="right">
        {!forbidManageBecauseSystemOrg && (
          <HasSomeRights rights={ORGANISATION_APP_RIGHTS}>
            {feature.requiresSubscription && (
              <>
                <Icon onClick={handleEditFeature} iconbutton size="large" name="pencil" />
                <ConfirmModal
                  header={t('areYouSureYouWantToRemoveAppFeatureSubscription')}
                  cancelContent={t('cancel')}
                  confirmContent={t('confirm')}
                  confirmColor="red"
                  onConfirm={onRemoveFeature}
                  feature={feature}
                  organisationApp={organisationApp}
                  trigger={
                    <Icon
                      iconbutton
                      size="large"
                      name="close"
                      feature={feature}
                      organisationApp={organisationApp}
                    />
                  }
                />
              </>
            )}
          </HasSomeRights>
        )}
      </Table.Cell>
    </Table.Row>
  );
};

AppSubscriptionFeature.propTypes = {
  urlRoot: PropTypes.string.isRequired,
  organisationApp: PropTypes.object.isRequired,
  feature: PropTypes.object.isRequired,
  handleRemoveFeature: PropTypes.func.isRequired,
  forbidManageBecauseSystemOrg: PropTypes.bool.isRequired,
};

export default AppSubscriptionFeature;
